// run_start — how a `POST /apps/:slug/run` answer is read.
//
// A 409 is NOT a failure to throw away: it means a build is STILL RUNNING on
// this app, and it carries that run's id. The server used to abort the live run
// and start the new one — which is how a lost stream plus a typed "continue"
// killed a healthy build (ridepool, 2026-09-12). Now the caller REATTACHES to the
// run it was told about; starting over is an explicit Stop, then Run.
//
// Pure (status + parsed body → result, or a thrown Error) so the contract is
// testable without a browser: api.ts does the fetch and hands the answer here.
import { askViewFromServer, type AskView } from "./ask_view";

export type RunStart =
  | { conflict: false; answered?: false; run_id: string; events_url: string }
  | { conflict: true;  run_id: string; events_url: string; error: string }
  // The answer was RECORDED and no run started: other questions are still waiting, and this is
  // the next one (platform-service: one question at a time, ONE run once the last is answered).
  | { conflict: false; answered: true; next: AskView };

export function parseRunStart(status: number, body: unknown, statusText = ""): RunStart {
  const j = (body && typeof body === "object") ? (body as Record<string, unknown>) : null;
  // 409 + a run id = the reattach signal. A 409 WITHOUT one is some other
  // conflict and is an error like any other — never a reattach to nothing.
  if (status === 409 && typeof j?.run_id === "string") {
    return {
      conflict:   true,
      run_id:     j.run_id,
      events_url: typeof j.events_url === "string" ? j.events_url : "",
      error:      typeof j.error === "string" ? j.error : "a build is still running",
    };
  }
  if (status < 200 || status >= 300) {
    // `message` is the sentence written for a person (a plan's refusal says the
    // numbers and the renewal date); `error` is the code. Show the sentence.
    throw new Error(typeof j?.message === "string" ? j.message : typeof j?.error === "string" ? j.error : (statusText || `HTTP ${status}`));
  }
  if (j?.answered === true) {
    const next = askViewFromServer(j.ask_pending);
    if (next === null) throw new Error("the answer was recorded but the next question could not be read");
    return { conflict: false, answered: true, next };
  }
  if (typeof j?.run_id !== "string") throw new Error("run started but no run_id was returned");
  return { conflict: false, run_id: j.run_id, events_url: typeof j.events_url === "string" ? j.events_url : "" };
}
